import { Card, CardContent } from "@components/shadcn-ui/card";
import { MobileFileManager } from "features/createApplication/components/DisplayApplicationIDContent";
import useDownloadPDF from "@customHooks/useDownloadPDF";
import { ResourceNotes } from "../../features/resourceNotes/ResourceNotes";
import { useEffect, useRef, useState } from "react";
import { ApplicationDetailsInterface } from "features/applications/ApplicationList";
import { applicationService } from "features/createApplication/service/applicationService";
import { useNavigate, useParams } from "react-router-dom";
import { decodeURIComponent, encodeURIComponent } from "@lib/utils";
import Snipper from "@components/ui/Snipper";
import { Button } from "@components/shadcn-ui/button";
import { useToast } from "@customHooks/useToast";
import PermissionWrapper from "features/Permission/PermissionWrapper";
import { Action, ApplicationState } from "features/Permission/types";
import {
  ApprovedLoanSection,
  ApprovedLoanSectionRef,
} from "../../features/resourceNotes/components/ApprovedLoanSection";
import { FileUploadComponent } from "features/createApplication/components/FileUploadComponent";
import { TablePaste } from "features/applications/components/TablePaste";
import { BankDisplay } from "features/createApplication/components/BankDisplay";
import { APPLICATION_STATUS, getStatusColor, PRODUCT_TYPES, } from "features/applications/config";
import { Badge } from "@components/shadcn-ui/badge";
import { ApplicationTasks } from "../../features/tasks/components/ApplicationTasks";
import { AddressDisplay } from "features/createApplication/components/AddressDisplay";
import { ReferenceDisplay } from "features/createApplication/components/ReferenceDisplay";
import { AddressResponse, addressService } from "../../features/createApplication/service/addressService";
import { Loader2, ExternalLink, Users, MapPin, UserCheck, Briefcase } from "lucide-react";
import { Group } from "features/groups/types";
import { getGroup } from "features/groups/services/groupsService";

const formatKey = (key: string) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

const renderValue = (value: string | number | boolean) => {
  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }
  return value || "";
};

// New helper to format enum-like values (e.g., PERSONAL_LOAN -> Personal Loan)
const formatEnumLabel = (val?: string) => {
  if (!val) return "";
  return val
    .toLowerCase()
    .split(/[_\s]+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

// New: map product type to label + color classes
const getProductType = (pt?: string) => {
  const defaultVal = { label: formatEnumLabel(pt), colorClass: "bg-gray-100 text-gray-800" };

  if (!pt) return defaultVal;

  const map: Record<string, { label: string; colorClass: string }> = {
    PERSONAL_LOAN: { label: "Personal Loan", colorClass: "bg-emerald-100 text-emerald-800" },
    HOME_LOAN: { label: "Home Loan", colorClass: "bg-blue-100 text-blue-800" },
    CAR_LOAN: { label: "Car Loan", colorClass: "bg-indigo-100 text-indigo-800" },
    BUSINESS_LOAN: { label: "Business Loan", colorClass: "bg-yellow-100 text-yellow-800" },
    CONSUMER_DURABLES_LOAN: { label: "Consumer Durables", colorClass: "bg-purple-100 text-purple-800" },
    GROUP_LOAN: { label: "Group Loan", colorClass: "bg-orange-100 text-orange-800" },
    // fallback for any other product types (use readable label)
    DEFAULT: defaultVal,
  };

  return map[pt] ?? { label: formatEnumLabel(pt), colorClass: "bg-gray-100 text-gray-800" };
};

// Temporary change appId as props
export const ApplicationDetailsPage = ({ appId }: { appId?: string }) => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { sectionRef } = useDownloadPDF();
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const approvedLoanSectionRef = useRef<ApprovedLoanSectionRef>(null);

  const [applicationDetails, setApplicationDetails] =
    useState<ApplicationDetailsInterface | null>(null);

  const [coApplicantAddresses, setCoApplicantAddresses] = useState<AddressResponse[]>([]);
  const [coApplicantAddressesMap, setCoApplicantAddressesMap] = useState<Record<string, AddressResponse>>({});
  const [isLoadingCoApplicantAddresses, setIsLoadingCoApplicantAddresses] = useState(false);

  // Group details state
  const [fullGroupDetails, setFullGroupDetails] = useState<Group | null>(null);
  const [isLoadingGroup, setIsLoadingGroup] = useState(false);

  const decodedApplicationId = applicationId
    ? decodeURIComponent(applicationId).toString()
    : appId;
  console.log(decodedApplicationId);

  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (!decodedApplicationId) {
        console.error("Application ID is undefined");
        return;
      }

      try {
        const applicationDetails = await applicationService.getApplicationById(
          decodedApplicationId
        );

        // Normalize coApplicantDetails to an array and filter empty entries
        const normalizeCoApplicants = (input: any) => {
          if (!input) return [];
          if (Array.isArray(input)) return input;
          return [input];
        };

        const isValidCoApplicant = (c: any) => {
          if (!c) return false;
          const firstName = c.personalDetails?.firstName?.trim() || "";
          const lastName = c.personalDetails?.lastName?.trim() || "";
          const hasName = firstName !== "" || lastName !== "";
          const hasKyc = c.kycDetails && Object.values(c.kycDetails).some((v: any) => typeof v === 'string' ? v.trim() !== '' : !!v);
          const hasAddress = Array.isArray(c.addressId) && c.addressId.some((id: any) => typeof id === 'string' && id.trim() !== '');
          const hasRelation = typeof c.relationToApplicant === 'string' && c.relationToApplicant.trim() !== '';
          return hasName || hasKyc || hasAddress || hasRelation;
        };

        const rawCoApplicants = normalizeCoApplicants(applicationDetails.coApplicantDetails);
        const validCoApplicants = rawCoApplicants.filter(isValidCoApplicant);

        // Update stored application details to have normalized coApplicantDetails array (may be empty)
        const updatedAppDetails = { ...applicationDetails, coApplicantDetails: validCoApplicants };
        setApplicationDetails(updatedAppDetails);

        // Collect all addressIds from valid co-applicants and fetch addresses if any
        const allAddressIds: string[] = validCoApplicants.flatMap((c: any) => Array.isArray(c.addressId) ? c.addressId : (c.addressId ? [c.addressId] : [])).filter(Boolean);
        if (allAddressIds.length > 0) {
          fetchCoApplicantAddresses(allAddressIds);
        }

      } catch (error) {
        console.error("Error fetching application details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCoApplicantAddresses = async (addressIds: string[] | string) => {
      if (!decodedApplicationId) return;

      setIsLoadingCoApplicantAddresses(true);
      try {
        const ids = Array.isArray(addressIds) ? addressIds : [addressIds];
        const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
        const fetchedMap: Record<string, AddressResponse> = {};

        for (const addressId of uniqueIds) {
          try {
            const address = await addressService.getAddressById(decodedApplicationId, addressId);
            if (address) {
              // ensure we key by the actual id returned (fallback to requested id)
              fetchedMap[address.id || addressId] = address;
            }
          } catch (error) {
            console.error(`Error fetching address ${addressId}:`, error);
          }
        }

        // merge with any previously fetched addresses
        setCoApplicantAddressesMap((prev) => ({ ...prev, ...fetchedMap }));
      } catch (error) {
        console.error("Error fetching co-applicant addresses:", error);
      } finally {
        setIsLoadingCoApplicantAddresses(false);
      }
    };

    if (decodedApplicationId) {
      fetchApplicationDetails();
    }
  }, [decodedApplicationId]);

  // Fetch group details when application is a GROUP_LOAN
  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (!applicationDetails) return;

      if (applicationDetails.productType === PRODUCT_TYPES.GROUP_LOAN) {
        const groupId = applicationDetails.groupDetails?.id || applicationDetails.leadId;
        const groupCity = applicationDetails.groupDetails?.city;

        if (!groupId) {
          console.log("No group ID available to fetch group details");
          return;
        }

        setIsLoadingGroup(true);
        try {
          // If we have both id and city from groupDetails, use them
          if (groupId && groupCity) {
            const group = await getGroup(groupId, groupCity);
            setFullGroupDetails(group);
          }
          // Otherwise, try to find the group using the leadId
          // For GROUP_LOAN, leadId IS the group ID, but we need city
          // We'll need to fetch all groups or have a different endpoint
          // For now, if we don't have city, we can't fetch the group
          else {
            console.warn("Group city is missing, cannot fetch group details. Need both id and city.");
            console.log("Available data:", { groupId, groupCity, leadId: applicationDetails.leadId });
          }
        } catch (error) {
          console.error("Error fetching group details:", error);
        } finally {
          setIsLoadingGroup(false);
        }
      }
    };

    fetchGroupDetails();
  }, [applicationDetails]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 w-full h-full">
        <Snipper />
        <p>Fetching application details...</p>
      </div>
    );
  }

  const SubmitApplication = async () => {
    const isConfirmed = window.confirm(
      "Once you submit the application, you will not be able to modify it. Do you want to proceed?"
    );

    if (!isConfirmed) {
      // If the user cancels, just return
      return;
    }

    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.SUBMIT_APPLICATION,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Your application has been submitted successfully!",
      });

      if (applicationDetails?.productType === PRODUCT_TYPES.GROUP_LOAN) {
        navigate(
          `/dashboard/groups/${encodeURIComponent(applicationDetails!.leadId)}?city=${encodeURIComponent(applicationDetails?.groupDetails?.city || "")}`
        );
      } else {
        navigate(
          `/dashboard/lead-details/${encodeURIComponent(
            applicationDetails!.leadId
          )}`
        );
      }
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in Saving application:", error);
    }
  };

  const PushBackActionHandler = async () => {
    try {
      await applicationService.performAction(
        {
          ...applicationDetails,
          loanApprovalDetails: {},
        }!,
        Action.REQUEST_EDIT_APPLICATION,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Application has been move to draft state SuccessFully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in Saving application:", error);
    }
  };

  const SoftApprovalHandler = async (formData: any) => {
    console.log("Form soft approved successfully with data:", formData);

    try {
      // Avoid unnecessary re-renders by not updating state unnecessarily
      await applicationService.performAction(
        {
          ...applicationDetails,
          loanApprovalDetails: {
            ...formData,
          },
        }!,
        Action.SOFT_APPROVE,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description:
          "Application state has been changed to soft approved state Successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in Saving application:", error);
    }
  };

  const viewVerificationHandler = async () => {
    const appId = applicationDetails!.id;

    navigate(`/dashboard/residence-verification/view/${appId}`);
  };

  const startVerificationHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.START_VERIFICATION,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Verification is successfully started!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in starting verification process:", error);
    }
  };

  const SanctionHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.SANCTION,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Application has been sanctioned successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in sanctioning application:", error);
    }
  };

  const InvoiceCollectedHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.INVOICE_COLLECTED,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Invoice has been collected successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in marking invoice as collected:", error);
    }
  };

  const MandateHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.MANDATE,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Mandate has been processed successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in processing mandate:", error);
    }
  };

  const SignHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.SIGN,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Application has been signed successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in signing application:", error);
    }
  };

  const DisburseHandler = async () => {
    try {
      await applicationService.performAction(
        applicationDetails!,
        Action.DISBURSE,
        applicationDetails!.leadId
      );

      toast({
        variant: "default",
        description: "Loan has been disbursed successfully!",
      });
      navigate("/dashboard/applications");
    } catch (error) {
      toast({
        variant: "destructive",
        description: "Something went wrong... Please try again",
      });
      console.error("Error in disbursing loan:", error);
    }
  };

  // Format address for display
  const formatAddress = (address: AddressResponse) => {
    const parts = [
      address.type,
      address.line1,
      address.line2,
      address.landmark,
      address.city,
      address.state,
      address.postalCode,
      address.country
    ].filter(Boolean);

    return parts.join(", ");
  };

  const handleCreateTask = () => {
    navigate(`/dashboard/application/details/${decodedApplicationId}/create-task?resourceType=APPLICATION_TASK`);
  };

  if (!applicationDetails) return <>Something went wrong..</>;

  const {
    productType,
    personalDetails,
    groupDetails,
    kycDetails,
    loanRequirementDetails,
    loanApprovalDetails = {},
  } = applicationDetails;

  const applicationSectionDetails = {
    personalDetails: personalDetails,
    groupDetails: productType === PRODUCT_TYPES.GROUP_LOAN ? groupDetails : undefined,
    kycDetails,
    loanRequirementDetails,
    loanApprovalDetails,
  };

  const sectionLength = Object.entries(applicationSectionDetails).filter(
    ([, sectionData]) => sectionData && Object.keys(sectionData).length > 0
  ).length;

  const handleSoftApproval = async () => {
    if (approvedLoanSectionRef.current) {
      await approvedLoanSectionRef.current.submitForm();
    }
  };

  console.log("ProductType >>>>", productType);

  return (
    <div>
      <div
        className="w-[91vw] md:w-[79.5vw] h-full max-w-full max-h-full flex flex-col bg-white p-1 md:p-0"
        ref={sectionRef as React.RefObject<HTMLDivElement>}
      >
        <div className="flex items-center space-x-4 pb-3 md:p-6 md:pb-0 p-3">
          <div className="flex items-center self-baseline space-x-4 rounded-full h-14 w-14">
            <img src="https://avatar.iran.liara.run/public" alt="Avatar" />
          </div>
          <div className="flex gap-5 md:flex-row justify-between flex-1">
            <span>
              <h2 className="text-xl font-semibold">
                {personalDetails.firstName}
              </h2>
              <p className="text-sm text-gray-500">{personalDetails.email}</p>
              <span className="flex items-center mt-2">
                <Badge
                  className={`rounded-sm px-1 font-normal  ${getStatusColor(
                    applicationDetails.state
                  )}`}
                >
                  {
                    APPLICATION_STATUS[
                    applicationDetails.state as keyof typeof APPLICATION_STATUS
                    ]
                  }
                </Badge>
                {/* Product type badge added beside status */}
                {(() => {
                  const productInfo = getProductType(productType);
                  return (
                    <Badge className={`rounded-sm px-1 ml-2 font-normal ${productInfo.colorClass}`}>
                      {productInfo.label}
                    </Badge>
                  );
                })()}
                {applicationDetails.rejectionDetails.rejectReason &&
                  applicationDetails.rejectionDetails.detailedReason && (
                    <>
                      <p className="text-sm text-gray-500 ml-2">
                        <strong>Reason:</strong>{" "}
                        <span className="mr-2">
                          {applicationDetails.rejectionDetails.rejectReason}
                        </span>
                        <span>
                          {applicationDetails.rejectionDetails.detailedReason}
                        </span>
                      </p>
                    </>
                  )}
              </span>
              {/* rejectionDetails: {
        rejectReason: selectedRejectReason,
        detailedReason: selectedRejectReason === "OTHER" ? detailedReason : "",
      }, */}
            </span>
            {/* Will enable this based on the need. Maybe in the next phase */}
            {/* <Button onClick={handleDownloadPDF}>Download PDF</Button> */}
          </div>
        </div>
        {/* TODO: create a new component for this */}
        <Card className="flex-grow md:p-6 overflow-y-auto border-none">
          <CardContent className="p-1">
            <div className="border border-gray-300 border-dashed rounded-md text-sm overflow-hidden mb-3">
              <div className="font-semibold bg-gray-100 p-2 flex items-center justify-between">
                <div>
                  <span className="text-sm">Application Id: </span>
                  <span className="text-sm font-medium">{applicationDetails.id}</span>
                </div>
                <div className="text-sm text-gray-600">
                  Created by{" "}
                  <span className="font-medium text-gray-800">
                    {applicationDetails.createdBy || "—"}
                  </span>
                </div>
              </div>
            </div>
            <div
              className={`grid gap-3 ${sectionLength === 1 ? "md:grid-cols-1" : "md:grid-cols-2"
                } md:gap-8`}
            >
              {Object.entries(applicationSectionDetails).map(
                ([sectionKey, sectionData]) => {
                  // Check if the section has content (i.e., not empty)
                  if (!sectionData || Object.keys(sectionData).length === 0) return null;
                  return (
                    <div
                      key={sectionKey}
                      className="border border-gray-300 border-dashed rounded-md text-sm overflow-hidden"
                    >
                      <div className="flex justify-between items-center bg-gray-100 p-2">
                        <h2 className="text-lg font-semibold">
                          {formatKey(sectionKey)}
                        </h2>
                        {sectionKey === "groupDetails" && (
                          <a
                            href={`/dashboard/groups/${encodeURIComponent((sectionData as any).id)}?city=${encodeURIComponent((sectionData as any).city)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="Open Group Details"
                          >
                            <ExternalLink className="h-5 w-5" />
                          </a>
                        )}
                      </div>
                      <ul className="divide-y divide-gray-200 p-2 divide-dashed">
                        {Object.entries(sectionData).map(([key, value]) => (
                          <li key={key} className="py-2 flex justify-between">
                            <span className="font-medium text-gray-600 capitalize flex-1">
                              {formatKey(key)}:
                            </span>
                            <span className="flex-1">{renderValue(value)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              )}
            </div>

            {/* Group Information Section - only for GROUP_LOAN */}
            {productType === PRODUCT_TYPES.GROUP_LOAN && fullGroupDetails && (
              <div className="mt-3">
                <strong className="block mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full flex justify-between items-center">
                  <span>Group Information</span>
                  <a
                    href={`/dashboard/groups/${encodeURIComponent(fullGroupDetails.id)}?city=${encodeURIComponent(fullGroupDetails.city)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    title="Open Group Details in new tab"
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span className="text-sm">View Full Details</span>
                  </a>
                </strong>
                <Card>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Group Name</p>
                          <p className="font-semibold">{fullGroupDetails.name}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <MapPin className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">City</p>
                          <p className="font-semibold">{fullGroupDetails.city}</p>
                        </div>
                      </div>

                      {fullGroupDetails.coordinator && (
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <UserCheck className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Coordinator</p>
                            <p className="font-semibold">
                              {fullGroupDetails.coordinator.contact.firstName} {fullGroupDetails.coordinator.contact.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{fullGroupDetails.coordinator.contact.phoneNumber}</p>
                          </div>
                        </div>
                      )}

                      {fullGroupDetails.bdManager && (
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <Briefcase className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">BD Manager</p>
                            <p className="font-semibold">{fullGroupDetails.bdManager.name}</p>
                            <p className="text-sm text-gray-500">{fullGroupDetails.bdManager.phoneNumber}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Loading state for group */}
            {productType === PRODUCT_TYPES.GROUP_LOAN && isLoadingGroup && (
              <div className="mt-3 flex items-center justify-center gap-2 p-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading group details...</span>
              </div>
            )}

            {/* Display Addresses */}
            {decodedApplicationId && (
              <div className="mb-3 mt-3">
                <strong className="block mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                  Address Details
                </strong>
                <AddressDisplay applicationId={decodedApplicationId} />
              </div>
            )}

            {/* Display Bank Accounts */}
            {decodedApplicationId && (
              <div className="mb-3 mt-3">
                <strong className="block mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                  Bank Account Details
                </strong>
                <BankDisplay applicationId={decodedApplicationId} />
              </div>
            )}

            {/* Display References */}
            {decodedApplicationId && applicationDetails.references && applicationDetails.references.length > 0 && (
              <div className="mb-3 mt-3">
                <strong className="block mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                  Reference Details
                </strong>
                <ReferenceDisplay
                  applicationId={decodedApplicationId}
                  references={applicationDetails.references}
                />
              </div>
            )}

            {/* Display Co-Applicant */}
            {decodedApplicationId && Array.isArray(applicationDetails.coApplicantDetails) && applicationDetails.coApplicantDetails.length > 0 && (
              <div className="mb-3 mt-3">
                <strong className="block mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                  Co-Applicant Details
                </strong>
                <div className="grid grid-cols-1 gap-4">
                  {applicationDetails.coApplicantDetails.map((coApplicant: any, index: number) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="bg-gray-50 p-3 flex items-center">
                          <div className="bg-primary/10 p-2 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </div>
                          <h3 className="ml-2 font-medium">Co-Applicant</h3>
                        </div>
                        <div className="p-4">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                            <div>
                              <p className="text-gray-500">Name</p>
                              <p className="font-medium">
                                {coApplicant.personalDetails.firstName} {coApplicant.personalDetails.lastName}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Relation</p>
                              <p className="font-medium">{coApplicant.relationToApplicant}</p>
                            </div>
                            {coApplicant.personalDetails.phoneNumber && (
                              <div>
                                <p className="text-gray-500">Phone Number</p>
                                <p className="font-medium">{coApplicant.personalDetails.phoneNumber}</p>
                              </div>
                            )}
                            {coApplicant.personalDetails.email && (
                              <div>
                                <p className="text-gray-500">Email</p>
                                <p className="font-medium">{coApplicant.personalDetails.email}</p>
                              </div>
                            )}
                            {coApplicant.kycDetails?.panNumber && (
                              <div>
                                <p className="text-gray-500">PAN Number</p>
                                <p className="font-medium">{coApplicant.kycDetails.panNumber}</p>
                              </div>
                            )}

                            {coApplicant.addressId && (
                              <div>
                                <p className="text-gray-500">Address IDs</p>
                                {isLoadingCoApplicantAddresses ? (
                                  <div className="flex items-center">
                                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                                    <span className="text-sm">Loading addresses...</span>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {(
                                      Array.isArray(coApplicant.addressId)
                                        ? coApplicant.addressId
                                        : [coApplicant.addressId]
                                    ).filter(Boolean).map((addrId: string, idx: number) => {
                                      const address = coApplicantAddressesMap[addrId];
                                      return (
                                        <div key={addrId + idx} className="text-sm">
                                          <p className="font-medium">
                                            <span className="font-semibold">Address {idx + 1}:</span>{" "}
                                            {address ? formatAddress(address) : <span className="text-gray-500">Address not found</span>}
                                          </p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <PermissionWrapper
              actions={Action.INVOICE_COLLECTED}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <strong className="block mb-0 mt-4 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm w-full">
                Upload Invoice
              </strong>
              <FileUploadComponent
                id={decodedApplicationId!}
                className="md:py-2"
              />
            </PermissionWrapper>

            {decodedApplicationId && (
              <div className="mb-2 mt-3">
                <div className="flex justify-between items-center bg-[#bbd0ce] p-2 rounded-sm">
                  <strong className="text-md">Tasks</strong>
                  <Button
                    onClick={() => handleCreateTask()}
                    className="bg-primary hover:bg-primary/90 h-8 text-sm"
                  >
                    Create Task
                  </Button>
                </div>
                <ApplicationTasks resourceId={decodedApplicationId} resourceType={"APPLICATION_TASK"} />
              </div>
            )}


            {decodedApplicationId && (
              <div className="mb-2 mt-3">
                <strong className="block mt-0 mb-2 text-md bg-[#bbd0ce] p-2 pl-4 rounded-sm">
                  Uploaded Documents
                </strong>
                <MobileFileManager applicationId={decodedApplicationId!} />
              </div>
            )}

            {decodedApplicationId && productType && (
              <ResourceNotes
                resourceId={decodedApplicationId}
                resourceType={productType}
              />
            )}

            <PermissionWrapper
              actions={[Action.REQUEST_EDIT_APPLICATION, Action.SOFT_APPROVE]}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <ApprovedLoanSection
                ref={approvedLoanSectionRef}
                onSubmitSuccess={SoftApprovalHandler}
              />
            </PermissionWrapper>
            <PermissionWrapper
              actions={[Action.COPY_PAST_TABLE]}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <TablePaste />
            </PermissionWrapper>
          </CardContent>
          {/* Buttons */}
          <div className="flex gap-2 justify-center flex-row mt-4">
            {/* TODO: Refactor */}
            {/* TODO: Create a hook maybe and use it in all places */}
            <PermissionWrapper
              actions={[Action.REQUEST_EDIT_APPLICATION, Action.SOFT_APPROVE]}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => PushBackActionHandler()}>
                Ask for edits
              </Button>
            </PermissionWrapper>
            <PermissionWrapper
              actions={Action.SOFT_APPROVE}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => handleSoftApproval()}>Soft Approve</Button>
            </PermissionWrapper>

            <PermissionWrapper
              actions={Action.SUBMIT_APPLICATION}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => SubmitApplication()}>
                Send to back office
              </Button>
            </PermissionWrapper>

            {/* New workflow buttons */}
            <PermissionWrapper
              actions={Action.SANCTION}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => SanctionHandler()}>Sanction</Button>
            </PermissionWrapper>

            <PermissionWrapper
              actions={Action.START_VERIFICATION}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => startVerificationHandler()}>Start Verification</Button>
            </PermissionWrapper>

            {PRODUCT_TYPES.CONSUMER_DURABLES_LOAN !== productType && (
              <PermissionWrapper
                actions={Action.VIEW_APPLICATION}
                applicationStatus={applicationDetails.state}
                productType={productType}
              >
                <Button onClick={() => viewVerificationHandler()}>View Residence Verifications</Button>
              </PermissionWrapper>
            )}

            <PermissionWrapper
              actions={Action.INVOICE_COLLECTED}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => InvoiceCollectedHandler()}>
                Mark Invoice Collected
              </Button>
            </PermissionWrapper>

            <PermissionWrapper
              actions={Action.MANDATE}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => MandateHandler()}>Process Mandate</Button>
            </PermissionWrapper>

            <PermissionWrapper
              actions={Action.SIGN}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => SignHandler()}>Sign</Button>
            </PermissionWrapper>

            <PermissionWrapper
              actions={Action.DISBURSE}
              applicationStatus={applicationDetails.state}
              productType={productType}
            >
              <Button onClick={() => DisburseHandler()}>Disburse</Button>
            </PermissionWrapper>
          </div>
        </Card>
      </div>
    </div>
  );
};